import { useTranslation } from 'react-i18next';
import { orderReference } from './orderReference';
import type { OrderMoneyStaffSummary } from '../../api/types';
import { parsePaise } from '../payments/allocation';
import { formatIstDate } from '../payments/paymentDates';
import { invoiceMoney } from './invoiceCopyText';
import './print.css';
export function SettlementStatement({
  summary: s,
  vehicleNumber,
  partnerGstin,
  partnerPan,
}: {
  summary: OrderMoneyStaffSummary;
  vehicleNumber?: string | null;
  partnerGstin?: string | null;
  partnerPan?: string | null;
}) {
  const { t } = useTranslation();
  const paid = s.allocations.filter(
    (a) => a.direction === 'OUT' && a.status === 'ACTIVE' && a.paidAt != null,
  );
  return (
    <article className="dnm-document" aria-label={t('invoices.settlementStatement.label')}>
      <header className="dnm-heading">
        <h1>{t('invoices.settlementStatement.brand')}</h1>
        <h2>{t('invoices.settlementStatement.heading')}</h2>
      </header>
      <p>
        <b>{t('invoices.settlementStatement.partner')}</b>{' '}
        {s.payeeInfo?.companyName ?? 'No partner assigned'}
      </p>
      {partnerGstin && <p>{t('invoices.settlementStatement.gstin', { gstin: partnerGstin })}</p>}
      {partnerPan && <p>{t('invoices.settlementStatement.pan', { pan: partnerPan })}</p>}
      <p>
        {orderReference(s.shipmentId)} · {s.route}
        {vehicleNumber && <> · {t('invoices.settlementStatement.vehicle', { vehicleNumber })}</>}
      </p>
      {s.partnerBillNumber && (
        <p>{t('invoices.settlementStatement.partnerBill', { bill: s.partnerBillNumber })}</p>
      )}
      {s.payeeInfo && (
        <p>
          {t('invoices.settlementStatement.bank', {
            account: s.payeeInfo.maskedAccount,
            ifsc: s.payeeInfo.ifscCode,
          })}
        </p>
      )}
      <table>
        <thead>
          <tr>
            <th>{t('invoices.settlementStatement.description')}</th>
            <th>{t('invoices.settlementStatement.amount')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{t('invoices.settlementStatement.contractedFreight')}</td>
            <td className="dnm-money">{invoiceMoney(s.partnerFreight)}</td>
          </tr>
          {s.charges
            .filter((c) => c.status === 'APPROVED' && parsePaise(c.partnerAmount) > 0n)
            .map((c) => (
              <tr key={c.id}>
                <td>{c.description || c.type}</td>
                <td className="dnm-money">{invoiceMoney(c.partnerAmount)}</td>
              </tr>
            ))}
          <tr>
            <th>{t('invoices.settlementStatement.partnerTotal')}</th>
            <td className="dnm-money">{invoiceMoney(s.partnerTotal)}</td>
          </tr>
        </tbody>
      </table>
      <h3>{t('invoices.settlementStatement.paidParts')}</h3>
      <table>
        <thead>
          <tr>
            <th>{t('invoices.settlementStatement.purpose')}</th>
            <th>{t('invoices.settlementStatement.date')}</th>
            <th>{t('invoices.settlementStatement.cash')}</th>
            <th>{t('invoices.settlementStatement.tds')}</th>
            <th>{t('invoices.settlementStatement.reference')}</th>
          </tr>
        </thead>
        <tbody>
          {paid.map((a) => (
            <tr key={a.id}>
              <td>{a.purpose}</td>
              <td>{formatIstDate(a.paidAt!)}</td>
              <td className="dnm-money">{invoiceMoney(a.amount)}</td>
              <td className="dnm-money">{invoiceMoney(a.tdsAmount)}</td>
              <td>{a.reference}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!paid.length && <p>{t('invoices.settlementStatement.noPaidParts')}</p>}
      <p>
        {t('invoices.settlementStatement.paidToPartner', { amount: invoiceMoney(s.paidToPartner) })}
      </p>
      <p>
        {t('invoices.settlementStatement.committed', {
          amount: invoiceMoney(s.committedToPartner),
        })}
      </p>
      <p>
        <b>
          {t('invoices.settlementStatement.balance', { amount: invoiceMoney(s.partnerRemaining) })}
        </b>
      </p>
      <footer>{t('invoices.settlementStatement.notTaxInvoice')}</footer>
    </article>
  );
}
